import { Box, Checkbox, Tooltip } from '@chakra-ui/react';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CalculationIconComponent from 'components/Icons/CalculationIcon';
import InputIconComponent from 'components/Icons/InputIcon';
import { FORMULA_ERROR, CURRENCY_OPTIONS } from 'projectConstants';
import {
  getTableCheckBox,
  handleTableCheckBoxChange,
  handleTableCheckBoxChangeAll,
} from 'redux/BuilderModeSlice';

import { AppDispatch } from './GlobalHelpers';

interface IndicatorIconProperties {
  indicatorType: string | undefined;
}

interface TooltopProperties {
  tooltipText: string;
  text: string;
  placeholder: string;
}

interface FormulaErrorBoxProperties {
  hasFormulaError: boolean;
  errorMessage?: any;
}

interface TableCheckBoxProperties {
  isHeading: boolean;
  itemId: number;
}

export const TooltipContent = ({ tooltipText, text, placeholder }: TooltopProperties) => {
  // Extract all unique currency symbols from CURRENCY_OPTIONS
  const currencySymbols = Array.from(new Set(CURRENCY_OPTIONS.map((option) => option.symbol)));
  // Combine with existing characters: %()-,.
  const allChars = [...currencySymbols, '%', '(', ')', ',', '.'];
  // Escape special regex characters for character class
  // In character class: - must be at end or escaped, ] \ ^ need escaping
  const escapedChars = allChars
    .filter((char) => char !== '-') // Remove - to add at end
    .map((char) => {
      if (char === ']') return '\\]';
      if (char === '\\') return '\\\\';
      if (char === '^') return '\\^';
      return char;
    });
  // Put - at the end (no escape needed when at end of character class)
  const pattern = `${escapedChars.join('')}-`;
  const cleanedText = text?.replace(new RegExp(`[${pattern}]`, 'g'), '').trim();
  // Check if it's a valid number and doesn't contain any alphabets
  const isNumber = /^-?\d+(\.\d+)?$/.test(cleanedText);
  return (
    <Tooltip label={tooltipText}>
      <p
        style={{
          width: '100%',
          paddingLeft: '.2rem',
          textAlign: isNumber ? 'right' : 'start',
        }}
      >
        {`${text}` || placeholder || ''}
      </p>
    </Tooltip>
  );
};

export const IndicatorIcon = ({ indicatorType }: IndicatorIconProperties) => {
  if ((indicatorType?.toLowerCase() ?? '') === 'calculation') {
    return <CalculationIconComponent color='#4BB6B9' style={{ margin: '0 6px' }} />;
  }
  if ((indicatorType?.toLowerCase() ?? '') === 'input') {
    return <InputIconComponent color='#9C89DD' style={{ margin: '0 6px' }} />;
  }
  return null;
};

export const FormulaErrorBox = ({ hasFormulaError, errorMessage }: FormulaErrorBoxProperties) =>
  hasFormulaError ? (
    <Box ml={4} maxW={'max-content'} color={'red'} fontSize={'2xs'} position={'relative'}>
      <Tooltip label={errorMessage || FORMULA_ERROR} placement={'top'} variant={'primary'}>
        <InfoOutlineIcon height={5} width={5} />
      </Tooltip>
    </Box>
  ) : null;

export const TableCheckBox = ({ isHeading, itemId }: TableCheckBoxProperties) => {
  const dispatch: AppDispatch = useDispatch();
  const tableCheckBox: any = useSelector(getTableCheckBox);

  const allChecked = useMemo(() => {
    return tableCheckBox?.[tableCheckBox?.length && tableCheckBox.length - 2]?.status;
  }, [tableCheckBox?.[tableCheckBox?.length && tableCheckBox.length - 2]?.status]);

  const isIndeterminate = useMemo(() => {
    return tableCheckBox?.[tableCheckBox?.length && tableCheckBox.length - 1]?.status;
  }, [tableCheckBox?.[tableCheckBox?.length && tableCheckBox.length - 1]?.status]);

  const desiredObject = useMemo(
    () => tableCheckBox.find((object: any) => object.id === itemId),
    [tableCheckBox, itemId],
  );

  return (
    <>
      {isHeading ? (
        <Checkbox
          variant={'delete'}
          className='delete-all-checkbox'
          isChecked={allChecked}
          isIndeterminate={isIndeterminate}
          onChange={(event) => {
            dispatch(handleTableCheckBoxChangeAll({ status: event.target.checked }));
          }}
        ></Checkbox>
      ) : (
        <Checkbox
          variant={'delete'}
          isChecked={desiredObject?.status}
          onChange={(event) => {
            dispatch(handleTableCheckBoxChange({ itemId, status: event.target.checked }));
          }}
        ></Checkbox>
      )}
    </>
  );
};
